import styled from "@emotion/styled";
import { motion, PanInfo } from "framer-motion";
import React, { PropsWithChildren, ReactElement } from "react";
import { fmap } from "../../utils/fmap";
import ConditionalAnimatePresence from "../ConditionalAnimatePresence";
import { AppOverlay } from "./AppOverlay";
import { If } from "./If";

export interface AppBottomSheetProps {
  isOpen: boolean;
  title?: string;
  closeOnDrag?: boolean;
  height?: string;
  withAnimatePresence?: boolean;
  onRequestClose: () => void;
}

export function AppDragableBottomSheet(
  props: PropsWithChildren<AppBottomSheetProps>
): ReactElement {
  const swipeConfidenceThreshold = 10000;
  const swipePower = (offset: number, velocity: number) => {
    return Math.abs(offset) * velocity;
  };
  const handleAnimationComplete = () => {
    props.onRequestClose();
  };

  const [contentRef, setContentRef] = React.useState<HTMLDivElement | null>(null);

  const handleDrag = (info: PanInfo) => {
    const swipe = swipePower(info.offset.y, info.velocity.y);

    if (swipe > swipeConfidenceThreshold && props.closeOnDrag === true) {
      handleAnimationComplete();
    }
  };

  return (
    <AppOverlay>
      <Wrapper isOpen={props.isOpen}>
        <ConditionalAnimatePresence enabled={props.withAnimatePresence ?? true}>
          {props.isOpen && (
            <>
              <Dimmer
                exit={{ opacity: 0 }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                onClick={props.onRequestClose}
              />
              <AppDragableBottomSheet.Root
                key="modal"
                drag="y"
                dragConstraints={{ top: 0, bottom: 0 }}
                dragElastic={{ top: 0.05, bottom: 1 }}
                initial={{ y: "100%", scale: 0.95 }}
                animate={{
                  y: 0,
                  scale: 1,
                  height: props.height ?? fmap(contentRef, (x) => x.clientHeight + 42) ?? "75vh",
                }}
                exit={{ y: "100%" }}
                transition={{ type: "spring", stiffness: 500, damping: 50, duration: 1 }}
                onDragEnd={(_e, info) => handleDrag(info)}
              >
                <AppDragableBottomSheet.Bar />
                <If condition={props.title !== undefined} comment="if popover has title">
                  <div className="AppBottomSheet_Header">
                    <h2 className="AppBottomSheet_Title">{props.title}</h2>
                  </div>
                </If>
                <AppDragableBottomSheet.Content ref={setContentRef}>
                  {props.children}
                </AppDragableBottomSheet.Content>
              </AppDragableBottomSheet.Root>
            </>
          )}
        </ConditionalAnimatePresence>
      </Wrapper>
    </AppOverlay>
  );
}

const Dimmer = styled(motion.div)`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: rgba(40, 56, 71, 0.8);
  backdrop-filter: blur(2px);
`;

const Wrapper = styled(motion.div)<{ isOpen: boolean }>`
  pointer-events: ${(props) => (props.isOpen ? undefined : "none")};
`;

AppDragableBottomSheet.Root = styled(motion.div)`
  background: white;
  border-radius: 32px 32px 0 0;
  z-index: 2;
  bottom: 0;
  position: fixed;
  right: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  width: 100%;

  &:before {
    background-color: white;
    content: "";
    height: 700px;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    transform: translateY(-1px);
  }
`;

AppDragableBottomSheet.Bar = styled.div`
  margin: 12px 0 24px 0;
  width: 95px;
  padding: 2.5px;
  background: #94a3b8;
  border-radius: 2.5px;
`;

AppDragableBottomSheet.Content = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  height: 100%;
  width: 100%;
`;
